<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12" md="2"></v-col>
      <v-col cols="12" sm="12" md="8">
        <v-card class="mx-auto my-5 mb-10" max-width="1000">
          <v-app-bar flat>
            <span class="headline">{{ formCompData.title }}</span>
          </v-app-bar>
          <v-card-text>
            <v-container>
              <formComponent
                ref="formComponent"
                :form="formComp"
                v-model="formComp.eventForm"
              />
            </v-container>
          </v-card-text>

          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="grey" outlined @click="closeform">{{
              $t("general.back")
            }}</v-btn>
            <v-btn color="primary" @click="saveForm(false)" data-cy="saveEvent">
              <div
                v-if="
                  $route.params.mode === 'new' || $route.params.mode === 'copy'
                "
              >
                {{ $t("general.save") }}
              </div>
              <div v-if="$route.params.mode === 'edit'">
                {{ $t("general.saveChanges") }}
              </div>
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- proof history temporarily hidden -->
        <v-card
          v-if="isEdit && false"
          class="mx-auto mb-10"
          max-width="1000"
          outlined
        >
          <v-card-title class="d-flex align-center">
            <span>{{ $t("riskAssessment.proof.timelineTitle") }}</span>
            <v-spacer />
            <v-chip
              v-if="proofsSorted.length"
              small
              label
              :color="latestOverdue ? 'error' : 'success'"
              outlined
            >
              <v-icon left small>
                {{
                  latestOverdue
                    ? "mdi-alert-circle-outline"
                    : "mdi-shield-check-outline"
                }}
              </v-icon>
              <template v-if="latestOverdue">
                {{ $t("riskAssessment.proof.overdue") }}
              </template>
              <template v-else>
                {{ $t("riskAssessment.proof.validUntil", { date: validUntil }) }}
              </template>
            </v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-0">
            <div v-if="!proofsSorted.length" class="grey--text pa-4">
              {{ $t("riskAssessment.proof.noProofs") }}
            </div>
            <template v-else>
              <div
                v-for="(p, i) in proofsSorted"
                :key="i"
                class="d-flex align-center px-4 py-3"
                :class="{ 'proof-row-divider': i > 0 }"
              >
                <span class="font-weight-medium">{{ p.name }}</span>
                <v-spacer />
                <span class="grey--text text--darken-1">{{ p.date }}</span>
              </div>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="2"></v-col>
    </v-row>
  </div>
</template>

<script>
function getDefaultData(kind = "basic") {
  return {
    kind,
    title: "",
    responsible: "",
    location: "",
    risks: [],
    riskAssessmentCategory: "",
    descriptionText: "",
    descriptionPoints: [],
    operatingInstruction: null,
    betriebsanweisung: null,
    pendingOperatingInstruction: null,
    additionalFiles: [],
  };
}

function withDialogs(item) {
  if (Array.isArray(item.risks)) {
    item.risks.forEach((risk) => {
      risk.dialogs = {
        classificationDialog: { open: false },
        afterActionDialog: { open: false },
      };
    });
  }
  return item;
}

import { newRisk } from "../../components/formSlotComponents/riskAssessment/newRisk";
import formComponent from "../../components/_utils/formComponent/formComponent.vue";
import { getUniqueId, dateUtil } from "../../components/api/api";

export default {
  components: { formComponent },
  props: ["data"],
  data() {
    return {
      formCompData: {
        title: "riskAssessment.newTitle",
        item: null,
      },
      formComp: {
        form: [],
        eventForm: getDefaultData(this.$route.query.kind === "advanced" ? "advanced" : "basic"),
      },
      users: [],
    };
  },
  computed: {
    isEdit() {
      return this.$route.params.mode === "edit";
    },
    isAdvanced() {
      return this.formComp.eventForm.kind === "advanced";
    },
    proofsSorted() {
      const proofs = (this.formComp.eventForm.proofs || []).slice();
      return proofs
        .sort((a, b) => new Date(b.proofedAt) - new Date(a.proofedAt))
        .map((p) => ({
          name: this.userName(p.user),
          date: dateUtil(new Date(p.proofedAt), false, "-"),
        }));
    },
    latestProofDate() {
      const proofs = this.formComp.eventForm.proofs || [];
      if (!proofs.length) return null;
      return proofs
        .map((p) => new Date(p.proofedAt).getTime())
        .sort((a, b) => b - a)[0];
    },
    latestOverdue() {
      if (!this.latestProofDate) return true;
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      return this.latestProofDate < oneYearAgo.getTime();
    },
    validUntil() {
      if (!this.latestProofDate) return "";
      const d = new Date(this.latestProofDate);
      d.setFullYear(d.getFullYear() + 1);
      return dateUtil(d, false, "-");
    },
  },
  created() {
    this.buildForm(this.formComp.eventForm.kind);
  },
  mounted() {
    this.switchFormModes(this.$route.params.mode, this.$route.params);
    if (this.isEdit) {
      this.$store.dispatch("getUsersData").then((users) => {
        this.users = users || [];
      });
    }
  },
  methods: {
    buildForm(kind) {
      const advanced = kind === "advanced";
      const form = [
        [
          {
            cols: 12,
            class: "pb-0 pt-4",
            id: getUniqueId(),
            componentName: "vTextField",
            config: {
              vModel: "title",
              label: this.$t("riskAssessment.title"),
              dataCy: "title",
            },
          },
        ],
        [
          {
            cols: 12,
            sm: 6,
            md: 6,
            class: "pb-0",
            id: getUniqueId(),
            componentName: "vTextField",
            config: {
              vModel: "location",
              label: this.$t("general.location"),
              dataCy: "location",
            },
          },
          {
            cols: 12,
            sm: 6,
            md: 6,
            class: "pb-0",
            id: getUniqueId(),
            componentName: "vSingleUserSelect",
            config: {},
          },
        ],
      ];

      if (advanced) {
        form.push(
          [
            {
              cols: 12,
              class: "pt-4",
              id: getUniqueId(),
              componentName: "riskAssessmentDescription",
              config: {},
            },
          ],
          [
            {
              cols: 12,
              class: "pt-4",
              id: getUniqueId(),
              componentName: "dangerousGoodOperatingInstruction",
              config: {},
            },
          ],
          [
            {
              cols: 12,
              class: "pt-4",
              id: getUniqueId(),
              componentName: "additionalFilesUpload",
              config: { downloadFolder: "Gefaehrdungsbeurteilung-Dokumente" },
            },
          ],
        );
      }

      form.push(
        [
          {
            cols: 12,
            sm: 12,
            md: 12,
            class: "pb-0",
            id: getUniqueId(),
            componentName: "riskAssessmentAreasPreset",
            config: { kind: advanced ? "advanced" : "basic" },
          },
        ],
        [
          {
            cols: 12,
            sm: 12,
            md: 12,
            class: "pb-0",
            id: getUniqueId(),
            componentName: "riskAssessmentRisks",
            config: {
              advanced,
            },
          },
        ],
      );

      this.formComp.form = form;
    },
    titleKey(mode) {
      const advanced = this.isAdvanced;
      if (mode === "edit") {
        return advanced
          ? "riskAssessment.kind.editAdvanced"
          : "riskAssessment.editRiskAssessment";
      }
      if (mode === "copy") {
        return advanced
          ? "riskAssessment.kind.copyAdvanced"
          : "riskAssessment.copyRiskAssessment";
      }
      return advanced ? "riskAssessment.kind.newAdvanced" : "riskAssessment.newTitle";
    },
    loadItem(item) {
      const kind = item.kind === "advanced" ? "advanced" : "basic";
      this.formComp.eventForm = {
        ...getDefaultData(kind),
        ...withDialogs(item),
        kind,
        pendingOperatingInstruction: null,
      };
      this.buildForm(kind);
    },
    userName(id) {
      const u = this.users.find((x) => String(x._id) === String(id));
      return u ? u.name : "—";
    },
    switchFormModes(mode, params) {
      switch (mode) {
        case "edit":
          this.$store
            .dispatch("getSingleRiskAssessment", params.id)
            .then((item) => {
              this.loadItem(item);
              this.formCompData.title = this.$t(this.titleKey("edit"));
            });
          break;
        case "copy": {
          this.$store
            .dispatch("getSingleRiskAssessment", params.id)
            .then((item) => {
              this.loadItem(item);
              this.formCompData.title = this.$t(this.titleKey("copy"));
              const form = this.formComp.eventForm;
              delete form._id;
              delete form.id;
              delete form.createdAt;
              delete form.createdBy;
              delete form.updatedAt;
              delete form.versions;
              delete form.currentVersion;
              delete form.proofs;
              form.additionalFiles = [];
              (form.risks || []).forEach((risk) => {
                risk.ticket = null;
              });
            });
          break;
        }
        case "new":
          this.formCompData.title = this.$t(this.titleKey("new"));
          if (this.$route.query.templateId) this.applyTemplate();
          break;
      }
    },
    async applyTemplate() {
      const tpl = await this.$store.dispatch(
        "getSingleRiskAssessmentTemplate",
        this.$route.query.templateId,
      );
      if (!tpl) return;
      const form = this.formComp.eventForm;
      form.title = tpl.name || "";
      form.risks = (tpl.risks || []).map((risk) =>
        newRisk(JSON.parse(JSON.stringify({ ...risk, _id: undefined }))),
      );
    },
    flattenDocumentReferences() {
      const toId = (reference) =>
        typeof reference === "object" && reference !== null
          ? reference._id || reference.id || null
          : reference || null;

      const form = this.formComp.eventForm;
      form.operatingInstruction = toId(form.operatingInstruction);
      form.betriebsanweisung = toId(form.betriebsanweisung);
    },
    async resolveInstructionCategory(name) {
      const response = await this.$store.dispatch(
        "getAllInstructionCategories",
      );
      const categories = response?.data || response || [];

      const existing = categories.find(
        (category) =>
          String(category.name ?? "")
            .trim()
            .toLowerCase() === name.toLowerCase(),
      );
      if (existing) return existing._id || existing.id;

      const created = await this.$store.dispatch("createInstructionCategory", {
        name: name,
      });
      return created ? created._id || created.id : null;
    },
    async applyPendingOperatingInstruction() {
      const pending = this.formComp.eventForm.pendingOperatingInstruction;
      if (!pending) return true;

      if (!pending.categoryName) {
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t(
            "dangerousGoods.dangerousGoodsList.form.instructionCategoryRequired",
          ),
        );
        return false;
      }

      const instructionCategory = await this.resolveInstructionCategory(
        pending.categoryName,
      );
      if (!instructionCategory) return false;

      const created = await this.$store.dispatch("createOperatingInstruction", {
        name: pending.name,
        filetype: pending.filetype,
        content: pending.content,
        instructionCategory: instructionCategory,
      });
      if (!created) return false;

      this.formComp.eventForm.operatingInstruction = created._id || created.id;
      this.formComp.eventForm.betriebsanweisung = null;
      this.formComp.eventForm.pendingOperatingInstruction = null;

      return true;
    },
    closeform() {
      this.resetData("new");
    },
    async saveForm(createNew) {
      this.formComp.eventForm.riskAssessmentCategory =
        this.$route.params.riskAssessmentCategoryId;

      if (!this.$refs.formComponent.validateAllFields()) return;

      if (this.isAdvanced) {
        if (!(await this.applyPendingOperatingInstruction())) return;
        this.flattenDocumentReferences();
      }

      if (
        this.$route.params.mode === "new" ||
        this.$route.params.mode === "copy"
      ) {
        this.$store
          .dispatch("createRiskAssessment", this.formComp.eventForm)
          .then((val) => {
            this.afterUpdateCheck(val, createNew, "new");
          });
      } else {
        this.$store
          .dispatch("updateRiskAssessment", this.formComp.eventForm)
          .then((val) => {
            this.afterUpdateCheck(val, createNew, "edit");
          });
      }
    },
    afterUpdateCheck(val, createNew, mode) {
      if (createNew) {
        //reset contact data to create new one
        this.resetData(mode);
      } else {
        //close form
        this.closeform();
      }
    },
    openDialog(val) {
      this.occupiedDialog.open = true;
      this.occupiedDialog.events = val;
    },
    resetData(mode) {
      //reset whole form data
      this.formComp.eventForm = getDefaultData();
      //reset form component form only after contact create
      if (mode === "new") {
        this.$refs.formComponent.resetForm();
      }
      //trigger reload data function in parent component
      this.reloadData();
      this.$router.back();
    },
    reloadData() {
      this.$emit("reloadData");
    },
  },
};
</script>

<style scoped>
.proof-row-divider {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
